// Converts a project's scenes to another video size: standard (16:9), mobile (9:16) or square (1:1).
//
// Each scene is re-laid out for the new shape, like a "magic resize":
//  - The scene is cut into blocks: rows (top to bottom), and in a row, side-by-side columns (for
//    example the texts on the left and the presenter on the right). Layers that overlap stay in the
//    same block, so a card keeps its icon and texts, a button its label.
//  - Narrower size (16:9 -> 9:16 or 1:1): the columns of a row are put under each other and every
//    block is enlarged to use the width. Wider size: the blocks are put side by side.
//  - Each block keeps its own design (everything in it is scaled together, texts included).
//  - Backgrounds filling the scene fill the new one; decorations at the edges stay at the edges.
//  - A to Z moves follow their layer (a presenter walking in still walks in from the side).
// When a scene cannot be cut into blocks, its layers keep their size and spread over the new shape.
//
// The scene is converted on a canvas of the new shape (canvasWidth / canvasHeight); the editor
// then fits that canvas to the screen as it does when a project is opened.

export const RATIOS = { "16:9": 16 / 9, "9:16": 9 / 16, "1:1": 1 };
export const SIZE_NAMES = { "16:9": "Standard Video (16:9)", "9:16": "Mobile Video (9:16)", "1:1": "Square Video (1:1)" };

/** Session key: the size a template should be converted to once the editor has opened it. */
export const CONVERT_KEY = "toons_convert_to";

const MAX_SCALE = 1.6; // a block is enlarged at most this much
const SPREAD = 0.5; // fallback: how much layers spread when the scene grows (half of it)

const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
const ok = (v) => typeof v === "number" && isFinite(v);

/** Layers drawn to fill their box whatever its shape (the editor's own SVG shapes). */
const stretchable = (layer) => layer && layer.type === "basicShape";

// ------------------------------------------------------------------------- boxes

function boxOf(layer) {
  const b = { x: num(layer.x), y: num(layer.y), w: num(layer.width), h: num(layer.height) };
  return [b.x, b.y, b.w, b.h].every(ok) ? b : null;
}

/** Where the layer rests in the scene: the end of its A to Z move (when it has one), else its box. */
function restBox(layer, b, W0, H0) {
  const m = layer.Motion;
  if (m && m.enabled !== false && m.repeat !== "pingpong" && m.repeat !== "loop" && ok(num(m.toXf)) && ok(num(m.toYf))) {
    return { x: num(m.toXf) * W0, y: num(m.toYf) * H0, w: b.w, h: b.h };
  }
  return b;
}

// ------------------------------------------------------------------------- text extent

let measureCtx = null;
function textWidth(str, size, weight, family) {
  try {
    if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
    measureCtx.font = `${weight || "400"} ${size}px ${family || "sans-serif"}`;
    return measureCtx.measureText(str).width;
  } catch (e) {
    return str.length * size * 0.55;
  }
}

/**
 * The part of a text layer's box its words really use (text boxes are often much wider than
 * their text): used to find the columns of a scene.
 */
function inkBox(layer, b) {
  if (!layer || layer.type !== "text") return b;
  const size = num(layer.fontSize);
  if (!ok(size) || size <= 0) return b;
  const raw = String(layer.text == null ? "" : layer.text).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");
  const lines = raw.split(/\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return b;
  const family = (layer.googleFontSetting && layer.googleFontSetting.fontName) || "sans-serif";
  const ls = num(layer.LetterSpacing);
  const widest = Math.max(
    ...lines.map((l) => textWidth(l, size, layer.fontWeight, family) + (ok(ls) ? ls * size * l.length : 0))
  );
  const w = Math.min(b.w, widest + 4);
  const x = layer.Align === "center" ? b.x + (b.w - w) / 2 : layer.Align === "right" ? b.x + b.w - w : b.x;
  return { x, y: b.y, w, h: b.h };
}

const union = (boxes) => {
  const x = Math.min(...boxes.map((b) => b.x));
  const y = Math.min(...boxes.map((b) => b.y));
  const r = Math.max(...boxes.map((b) => b.x + b.w));
  const t = Math.max(...boxes.map((b) => b.y + b.h));
  return { x, y, w: r - x, h: t - y };
};

/**
 * Groups items along x ("x") or y ("y"): items whose ranges overlap are one group. gap < 0 lets
 * ranges overlap a little and still be separate groups.
 */
function groupsAlong(items, axis, gap) {
  const a = axis === "x" ? "x" : "y";
  const s = axis === "x" ? "w" : "h";
  const sorted = items.slice().sort((p, q) => p.ink[a] - q.ink[a]);
  const groups = [];
  let end = -Infinity;
  sorted.forEach((it) => {
    const start = it.ink[a];
    if (!groups.length || start > end + gap) {
      groups.push([it]);
      end = start + it.ink[s];
    } else {
      groups[groups.length - 1].push(it);
      end = Math.max(end, start + it.ink[s]);
    }
  });
  return groups;
}

// ------------------------------------------------------------------------- converting a scene

function convertScene(slide, ratio, fromSize) {
  let W0 = num(slide.canvasWidth);
  let H0 = num(slide.canvasHeight);
  if (!(ok(W0) && ok(H0) && W0 > 0 && H0 > 0)) {
    const r = RATIOS[fromSize] || RATIOS["16:9"];
    W0 = r >= 1 ? 524 : 524 * r;
    H0 = r >= 1 ? 524 / r : 524;
  }
  const r0 = W0 / H0;
  let W1 = W0;
  let H1 = H0;
  if (ratio < r0 - 0.001) H1 = W0 / ratio;
  else if (ratio > r0 + 0.001) W1 = H0 * ratio;
  else return { ...slide };
  const narrower = ratio < r0;

  const layers = Array.isArray(slide.LayerData) ? slide.LayerData : [];
  const items = layers.map((layer, i) => {
    const b = boxOf(layer);
    if (!b) return { i, layer, kind: "keep" };
    const rest = restBox(layer, b, W0, H0);
    const ink = inkBox(layer, rest);
    const fillsW = b.w >= W0 * 0.92 && b.x <= W0 * 0.06;
    const fillsH = b.h >= H0 * 0.92 && b.y <= H0 * 0.06;
    let kind = "content";
    if (fillsW && fillsH) kind = "background";
    else if (fillsW || fillsH) kind = "band";
    else {
      // Drawn shapes partly off the scene (corner circles, dots) stay at their edge.
      const offEdge = rest.x < -2 || rest.y < -2 || rest.x + rest.w > W0 + 2 || rest.y + rest.h > H0 + 2;
      if (stretchable(layer) && offEdge) kind = "decor";
    }
    return { i, layer, b, rest, ink, kind };
  });

  // Fallback / decorations: keep the size, spread over the new shape (edges stay edges).
  const kx = W1 / W0 <= 1 ? 1 : 1 + (W1 / W0 - 1) * SPREAD;
  const ky = H1 / H0 <= 1 ? 1 : 1 + (H1 / H0 - 1) * SPREAD;
  const along = (p, size, S0, S1, k) => (p >= S0 ? S1 + (p - S0) : p + size <= 0 ? p : S1 / 2 + (p + size / 2 - S0 / 2) * k - size / 2);
  const point = (p, S0, S1, k) => (p > S0 ? S1 + (p - S0) : p < 0 ? p : S1 / 2 + (p - S0 / 2) * k);
  const spreadT = {
    box: (b) => ({ x: along(b.x, b.w, W0, W1, kx), y: along(b.y, b.h, H0, H1, ky), w: b.w, h: b.h }),
    point: (x, y) => ({ x: point(x, W0, W1, kx), y: point(y, H0, H1, ky) }),
    scale: 1,
  };

  // ---- blocks
  // Rows and columns are found from the real content (texts, characters, props, pictures); the
  // drawn shapes (cards, buttons, blobs, rings) then join the row / column they sit in, so a small
  // decoration between two columns does not glue them together.
  const content = items.filter((it) => it.kind === "content");
  const solid = content.filter((it) => !stretchable(it.layer));
  const soft = content.filter((it) => stretchable(it.layer));
  const gapX = -W0 * 0.03; // columns may overlap a little (text boxes, a character's edge)
  const gapY = 0; // rows only split where there is a real gap
  const rangeOf = (group, a, sz) => [Math.min(...group.map((it) => it.ink[a])), Math.max(...group.map((it) => it.ink[a] + it.ink[sz]))];
  /** Puts each extra item in the group whose range holds its centre (else the nearest group). */
  const attach = (groups, extra, a, sz) => {
    const ranges = groups.map((g) => rangeOf(g, a, sz));
    extra.forEach((it) => {
      const c = it.ink[a] + it.ink[sz] / 2;
      let best = 0;
      let bestD = Infinity;
      ranges.forEach(([lo, hi], k) => {
        const d = c < lo ? lo - c : c > hi ? c - hi : 0;
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      });
      groups[best].push(it);
    });
    return groups;
  };

  // Units: the content inside one drawn container (a card, a badge, a button) stays together.
  let units = solid.map((it) => ({ ink: it.ink, members: [it] }));
  const inside = (u, b) => {
    const cx = u.ink.x + u.ink.w / 2;
    const cy = u.ink.y + u.ink.h / 2;
    return cx >= b.x && cx <= b.x + b.w && cy >= b.y && cy <= b.y + b.h;
  };
  soft
    .filter((sh) => sh.ink.w * sh.ink.h < W0 * H0 * 0.35)
    .sort((p, q) => p.ink.w * p.ink.h - q.ink.w * q.ink.h) // smallest containers first
    .forEach((sh) => {
      const ins = units.filter((u) => inside(u, sh.ink));
      if (ins.length < 2) return;
      const merged = { ink: union(ins.map((u) => u.ink)), members: [].concat(...ins.map((u) => u.members)) };
      units = units.filter((u) => !ins.includes(u)).concat(merged);
    });
  const flat = (us) => [].concat(...us.map((u) => u.members));

  let blocks = []; // { items, box }
  let keepRows = null; // narrower: the rows kept whole (the other arrangement to compare)
  if (units.length) {
    const rowsU = groupsAlong(units, "y", gapY);
    const rows = attach(rowsU.map(flat), soft, "y", "h");
    const columnsOf = (k) => attach(groupsAlong(rowsU[k], "x", gapX).map(flat), rows[k].filter((it) => stretchable(it.layer)), "x", "w");
    if (narrower) {
      keepRows = rows.map((row) => ({ items: row }));
      rows.forEach((row, k) => {
        const cols = columnsOf(k);
        if (cols.length === 2) cols.forEach((c) => blocks.push({ items: c }));
        else if (cols.length >= 3) {
          // A row of 3+ alike blocks (cards, numbers: same size, same kinds of layers) stays together.
          const widths = cols.map((c) => union(c.map((it) => it.ink)).w);
          const makeup = (c) => c.map((it) => it.layer.type).sort().join(",");
          const similar = Math.max(...widths) <= Math.min(...widths) * 1.6 && cols.every((c) => makeup(c) === makeup(cols[0]));
          if (similar) blocks.push({ items: row });
          else cols.forEach((c) => blocks.push({ items: c }));
        } else blocks.push({ items: row });
      });
    } else if (rows.length >= 2) {
      // Wider: the rows become two groups placed side by side (e.g. texts | presenter).
      const heights = rows.map((r) => union(r.map((it) => it.ink)).h);
      const total = heights.reduce((a, b) => a + b, 0);
      let acc = 0;
      let split = 1;
      for (let k = 0; k < rows.length - 1; k++) {
        acc += heights[k];
        split = k + 1;
        if (acc >= total / 2) break;
      }
      blocks.push({ items: [].concat(...rows.slice(0, split)) });
      blocks.push({ items: [].concat(...rows.slice(split)) });
    } else if (rows.length === 1) {
      const cols = columnsOf(0);
      if (cols.length >= 2) cols.forEach((c) => blocks.push({ items: c }));
    }
  }
  const boxUp = (list) => list.forEach((bl) => (bl.box = union(bl.items.map((it) => it.ink))));
  boxUp(blocks);
  if (keepRows) boxUp(keepRows);
  const useBlocks = blocks.length >= 2 || (blocks.length === 1 && narrower);

  const T = new Map(); // item index -> transform
  if (useBlocks) {
    const margin = (narrower ? W1 : H1) * 0.06;
    const gap = (narrower ? H1 : W1) * 0.035;
    if (narrower) {
      // Under each other, each as wide as it can be (up to MAX_SCALE), then all fitted to the height.
      const stack = (list) => {
        let scales = list.map((bl) => Math.min(MAX_SCALE, (W1 - 2 * margin) / bl.box.w));
        const totalH = () => list.reduce((a, bl, k) => a + bl.box.h * scales[k], 0) + gap * (list.length - 1);
        const room = H1 - 2 * margin;
        if (totalH() > room) {
          const f = room / totalH();
          scales = scales.map((sc) => sc * f);
        }
        let y = (H1 - totalH()) / 2;
        list.forEach((bl, k) => {
          const sc = scales[k];
          const x = (W1 - bl.box.w * sc) / 2;
          bl.t = { s: sc, dx: x - bl.box.x * sc, dy: y - bl.box.y * sc };
          y += bl.box.h * sc + gap;
        });
        return Math.min(...scales);
      };
      // Columns under each other, or the rows kept whole: whichever leaves everything bigger
      // (on a square, a text beside a picture often reads better than both shrunk).
      const stacked = stack(blocks);
      if (keepRows && keepRows.length !== blocks.length) {
        const kept = stack(keepRows);
        if (kept > stacked * 1.05) blocks = keepRows;
        else stack(blocks);
      }
    } else {
      // Side by side, each as tall as it can be (up to MAX_SCALE), then all fitted to the width.
      let scales = blocks.map((bl) => Math.min(MAX_SCALE, (H1 - 2 * margin) / bl.box.h));
      const totalW = () => blocks.reduce((a, bl, k) => a + bl.box.w * scales[k], 0) + gap * (blocks.length - 1);
      const room = W1 - 2 * margin;
      if (totalW() > room) {
        const f = room / totalW();
        scales = scales.map((s) => s * f);
      }
      let x = (W1 - totalW()) / 2;
      blocks.forEach((bl, k) => {
        const s = scales[k];
        const y = (H1 - bl.box.h * s) / 2;
        bl.t = { s, dx: x - bl.box.x * s, dy: y - bl.box.y * s };
        x += bl.box.w * s + gap;
      });
    }
    blocks.forEach((bl) => {
      const { s, dx, dy } = bl.t;
      const tr = {
        box: (b) => ({ x: b.x * s + dx, y: b.y * s + dy, w: b.w * s, h: b.h * s }),
        point: (px, py) => ({ x: px * s + dx, y: py * s + dy }),
        scale: s,
      };
      bl.items.forEach((it) => T.set(it.i, tr));
    });
  }

  const out = items.map((it) => {
    const layer = it.layer;
    if (it.kind === "keep") return layer;
    const b = it.b;
    if (it.kind === "background") return fillBackground(layer, b, W0, H0, W1, H1);
    if (it.kind === "band") return fillBand(layer, b, W0, H0, W1, H1, spreadT);
    const tr = T.get(it.i) || spreadT;
    return moveLayer(layer, b, it.rest, tr, W0, H0, W1, H1);
  });

  return { ...slide, LayerData: out, canvasWidth: W1, canvasHeight: H1 };
}

// ------------------------------------------------------------------------- layer changes

function setBox(out, layer, nb) {
  out.x = nb.x;
  out.y = nb.y;
  out.width = nb.w;
  out.height = nb.h;
  // The editor's "original" box (what it rescales from).
  if ("originalX" in layer) out.originalX = nb.x;
  if ("originalY" in layer) out.originalY = nb.y;
  if ("originalWidth" in layer) out.originalWidth = nb.w;
  if ("originalHeight" in layer) out.originalHeight = nb.h;
}

function fillBackground(layer, b, W0, H0, W1, H1) {
  const out = { ...layer };
  if (stretchable(layer) || layer.type === "text") {
    setBox(out, layer, { x: b.x * (W1 / W0), y: b.y * (H1 / H0), w: b.w * (W1 / W0), h: b.h * (H1 / H0) });
  } else {
    // A picture / video covering the scene covers the new one, keeping its shape.
    const s = Math.max(W1 / W0, H1 / H0);
    const nw = b.w * s;
    const nh = b.h * s;
    setBox(out, layer, { x: (W1 - nw) / 2 + (b.x - (W0 - b.w) / 2) * s, y: (H1 - nh) / 2 + (b.y - (H0 - b.h) / 2) * s, w: nw, h: nh });
  }
  return out;
}

function fillBand(layer, b, W0, H0, W1, H1, spreadT) {
  const out = { ...layer };
  const nb = spreadT.box(b);
  if (b.w >= W0 * 0.92 && stretchable(layer)) {
    nb.x = b.x * (W1 / W0);
    nb.w = b.w * (W1 / W0);
  }
  if (b.h >= H0 * 0.92 && stretchable(layer)) {
    nb.y = b.y * (H1 / H0);
    nb.h = b.h * (H1 / H0);
  }
  setBox(out, layer, nb);
  return out;
}

function moveLayer(layer, b, rest, tr, W0, H0, W1, H1) {
  const out = { ...layer };
  const s = tr.scale;
  const m = layer.Motion;
  const moving = rest !== b;
  if (moving) {
    // Moves: the end (Z) follows the block; the start keeps its side when it was off the scene.
    const z = tr.box(rest);
    const start = { x: z.x + (b.x - rest.x) * s, y: z.y + (b.y - rest.y) * s, w: b.w * s, h: b.h * s };
    if (b.x + b.w <= 0) start.x = -start.w - 8;
    else if (b.x >= W0) start.x = W1 + 8;
    if (b.y + b.h <= 0) start.y = -start.h - 8;
    else if (b.y >= H0) start.y = H1 + 8;
    setBox(out, layer, start);
    const nm = { ...m, toXf: z.x / W1, toYf: z.y / H1 };
    const hx = num(m.hXf);
    const hy = num(m.hYf);
    if (ok(hx) && ok(hy)) {
      const p = tr.point(hx * W0, hy * H0);
      nm.hXf = p.x / W1;
      nm.hYf = Math.max(-0.5, p.y / H1);
    }
    out.Motion = nm;
  } else {
    setBox(out, layer, tr.box(b));
    if (m && typeof m === "object") {
      // Moves in place (floating back and forth): same move around the new place.
      const nm = { ...m };
      if (ok(num(m.toXf)) && ok(num(m.toYf))) {
        const p = tr.point(num(m.toXf) * W0, num(m.toYf) * H0);
        nm.toXf = p.x / W1;
        nm.toYf = p.y / H1;
      }
      out.Motion = nm;
    }
  }
  if (s !== 1) {
    if (ok(num(layer.fontSize))) out.fontSize = num(layer.fontSize) * s;
    if (ok(num(layer.originalFontSize))) out.originalFontSize = num(layer.originalFontSize) * s;
  }
  return out;
}

/**
 * The slides converted to `toSize` ("16:9", "9:16" or "1:1"). fromSize: the project's size now
 * (used only for scenes that do not say their size). The slides given are not changed.
 */
export function convertSlides(slides, toSize, fromSize) {
  const ratio = RATIOS[toSize];
  if (!ratio || !Array.isArray(slides)) return slides;
  return slides.map((slide) => (slide && typeof slide === "object" ? convertScene(slide, ratio, fromSize) : slide));
}
